import React, { useEffect, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthProvider";
import {
  isLimitedToOpsChatExperience,
  OPS_CHAT_LANDLORD_HOME,
} from "../helpers/accountProduct";

const V2_ENTRY_KEY = "lo_v2_entry";

const PostLoginRedirect = () => {
  const { user, userInfo, loading, isUserInfoLoading } =
    useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const lastAuthRedirectKeyRef = useRef(null);

  useEffect(() => {
    if (location.pathname !== "/login" && location.pathname !== "/signup") {
      lastAuthRedirectKeyRef.current = null;
    }
  }, [location.pathname]);

  useEffect(() => {
    // Only proceed once auth + user info are fully resolved
    if (!user || loading || isUserInfoLoading) return;

    // V2 bolt-on: highest-priority override. If the user arrived via the
    // AddressHook (sessionStorage flag set), send them directly to the
    // simulation screen regardless of which page they are currently on.
    // Clear the flag immediately so this runs exactly once.
    const isV2Entry = sessionStorage.getItem(V2_ENTRY_KEY) === "1";
    if (isV2Entry) {
      sessionStorage.removeItem(V2_ENTRY_KEY);
      navigate("/v2/simulation", { replace: true });
      return;
    }

    const protectedRoutes = [
      "/dashboard",
      "/units",
      "/tenants",
      "/rent",
      "/rent-dashboard",
      "/leases",
      "/repairs",
      "/leo",
      "/automation",
      "/offers",
      "/settings",
      "/profile",
      "/v2/",
    ];
    const isOnProtectedRoute = protectedRoutes.some((route) =>
      location.pathname.startsWith(route)
    );
    if (isOnProtectedRoute) return;

    // If user is on explicit auth pages, send them to the right home
    // (OpsChat-only vs full app vs V2 bolt-on)
    if (location.pathname === "/login" || location.pathname === "/signup") {
      const redirectKey = `${user.uid}:${location.pathname}`;
      if (lastAuthRedirectKeyRef.current === redirectKey) {
        return;
      }
      lastAuthRedirectKeyRef.current = redirectKey;

      if (userInfo?.productVersion === "v2_bolt_on") {
        navigate("/v2/simulation", { replace: true });
        return;
      }
      const opsOnly = userInfo && isLimitedToOpsChatExperience(userInfo);
      navigate(opsOnly ? OPS_CHAT_LANDLORD_HOME : "/dashboard", {
        replace: true,
      });
      return;
    }
  }, [user, loading, userInfo, isUserInfoLoading, navigate, location.pathname]);

  // This component doesn't render anything
  return null;
};

export default PostLoginRedirect;
